/**
 * A virtual account's card on the list (TMMAPP-0159 §E 15, 17: Virtual Accounts behave like My
 * Accounts; TMMAPP-0151 §F 17). It is My Accounts' card (`RealAccountCard`): the whole card opens the
 * virtual account's page (`/v/{id}`), as an account card opens the account. It holds the name, the
 * privacy chip (`spacePrivacy`, the one rule, from the served `sharedWithNames` / `ownerDisplayName`),
 * the served total (`balanceMinor`) in the owner's currency and the envelope count. A LOCKED owner's
 * row carries no figures: the Locked tag and the count only.
 *
 * An owned card keeps its reorder handle and its kebab: Rename, Move earlier / later.
 */
import { MenuItem } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { VirtualAccountListItem } from '@/api/virtualAccounts';
import type { ReorderHandleProps } from '@/components/SortableCardGrid';
import { ReorderHandle } from '@/components/SortableCardGrid';
import { CardFigure } from '@/features/accounts/AccountCards';
import { CardMenu, RealAccountCard } from '@/features/accounts/RealAccountCard';
import { useLocale } from '@/i18n/useLocale';
import { spacePrivacy } from '@/shell/privacy';

export interface OwnedActions {
  onRename: () => void;
  reorder: ReorderHandleProps;
}

interface VirtualAccountCardProps {
  virtualAccount: VirtualAccountListItem;
  /** Present for an owned active card — the handle and the kebab (rename, move earlier / later). */
  actions?: OwnedActions;
}

export function VirtualAccountCard({ virtualAccount, actions }: VirtualAccountCardProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const chip = spacePrivacy({ ...virtualAccount, sharedBy: null }, t, locale);
  const locked = virtualAccount.ownerAccess === 'LOCKED';
  const count = t('virtualAccounts.envelopeCount', { count: virtualAccount.envelopeCount });

  return (
    <RealAccountCard
      name={virtualAccount.name}
      to={`/v/${virtualAccount.id}`}
      chip={chip}
      meta={null}
      // The owner is locked (TMMAPP-0110): listed with no figures, its contents refused.
      tags={locked ? [t('shell.locked')] : []}
      balance={
        virtualAccount.balanceMinor === null ? null : (
          <CardFigure minor={virtualAccount.balanceMinor} currency={virtualAccount.currency} />
        )
      }
      line={count}
      handle={actions ? <ReorderHandle {...actions.reorder} corner="end" /> : undefined}
      {...(actions
        ? {
            menu: (
              <CardMenu
                title={virtualAccount.name}
                label={t('virtualAccounts.actions', { name: virtualAccount.name })}
              >
                {(close) => [
                  <MenuItem
                    key="rename"
                    onClick={() => {
                      close();
                      actions.onRename();
                    }}
                  >
                    {t('virtualAccounts.rename')}
                  </MenuItem>,
                  <MenuItem
                    key="earlier"
                    disabled={!actions.reorder.canMoveEarlier}
                    onClick={() => {
                      close();
                      actions.reorder.onMoveEarlier();
                    }}
                  >
                    {t('reorder.moveEarlier')}
                  </MenuItem>,
                  <MenuItem
                    key="later"
                    disabled={!actions.reorder.canMoveLater}
                    onClick={() => {
                      close();
                      actions.reorder.onMoveLater();
                    }}
                  >
                    {t('reorder.moveLater')}
                  </MenuItem>,
                ]}
              </CardMenu>
            ),
          }
        : {})}
    />
  );
}
